import { useEffect, useState } from 'react';

export type UseConnectOauthTokenParams<
  T extends (...params: any[]) => Promise<string | undefined>,
> = [
  popupName: string,
  popupEventType: string,
  getOAuthUrl: T,
  onDone: () => void,
  popupWindowProps?: {
    width?: number;
    height?: number;
  },
  target?: string,
];
export const useConnectOauthToken = <
  T extends (...params: any[]) => Promise<string | undefined>,
>(
  ...hookParams: UseConnectOauthTokenParams<T>
): {
  connect: (...params: Parameters<T>) => Promise<void>;
  popupOpen: boolean;
} => {
  const [
    popupName,
    popupEventType,
    getOAuthUrl,
    onDone,
    popupWindowProps,
    target,
  ] = hookParams;
  const [popup, setPopup] = useState<Window | null>(null);

  useEffect(() => {
    if (!popup) {
      return;
    }
    const onMessage = (event: any) => {
      if (event.origin !== new URL(API_URL).origin) {
        return;
      }
      const { data } = event;
      if (data?.type === popupEventType) {
        popup.close();
        setPopup(null);
        onDone();
      }
    };
    window.addEventListener('message', onMessage);
    const checkIfPopupClosedInterval = setInterval(() => {
      if (popup.closed) {
        setPopup(null);
      }
    }, 1000);
    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(checkIfPopupClosedInterval);
    };
  }, [popup, setPopup, onDone, popupEventType]);

  const connect = async (...params: Parameters<T>) => {
    const url = await getOAuthUrl(...params);
    if (url === undefined) {
      return;
    }
    const popup = window.open(
      url,
      target,
      `width=${popupWindowProps?.width || 500},height=${
        popupWindowProps?.height || 800
      }`,
    );
    setPopup(popup);
  };
  return { connect, popupOpen: !!popup };
};
